import './TopBar.scss';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setAppTheme, setSidebarMobileOpen } from '../../storeRedux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routeConfig from './routeConfig';
import Breadcrumbs from './Breadcrumbs';
import RefreshIndicator from '../RefreshIndicator/RefreshIndicator';
import LanguageModal from '../LanguageModal/LanguageModal';
import { Bars3Icon, SunIcon, MoonIcon, ExclamationTriangleIcon } from './icons';
import { LANGUAGES } from '../../i18n';

const DollarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

export default function TopBar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [langModalOpen, setLangModalOpen] = useState(false);

  const theme = useSelector((state) => state.APP_THEME);
  const userName = useSelector((state) => state.USER_NAME);
  const userRole = useSelector((state) => state.USER_ROLE);
  const usdtBalance = useSelector((state) => state.USDT_BALANCE);
  const rapiraPayableBalance = useSelector((state) => state.RAPIRA_PAYABLE_BALANCE);
  const isFrozen = useSelector((state) => state.IS_FROZEN);
  const freezeKind = useSelector((state) => state.FREEZE_KIND);
  const rates = useSelector((state) => state.RATES);

  const currentRoute = routeConfig[location.pathname];
  const pageTitle = currentRoute ? t(currentRoute.labelKey) : '';

  const toggleTheme = () => {
    dispatch(setAppTheme(theme === 'light' ? 'dark' : 'light'));
  };

  const openMobileSidebar = () => {
    dispatch(setSidebarMobileOpen(true));
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const showFreezeAlert = userRole === 'operator' && isFrozen;
  const showBalanceAlert = userRole === 'operator' && usdtBalance <= 150 && !isFrozen;
  const hasAlert = showFreezeAlert || showBalanceAlert;

  return (
    <div className="TopBar">
      <div className="TopBar__left">
        <button className="TopBar__burger" onClick={openMobileSidebar}>
          <Bars3Icon />
        </button>
        <div className="TopBar__title-area">
          <h1 className="TopBar__page-title">{pageTitle}</h1>
          <Breadcrumbs />
        </div>
      </div>

      {/* Alert area */}
      {hasAlert && (
        <div className="TopBar__alert">
          <ExclamationTriangleIcon />
          <span>
            {showFreezeAlert && <>{t('layout:topbar.accountFrozen')} ({freezeKind}). </>}
            {((isFrozen && usdtBalance <= 150) || showBalanceAlert) && <>{t('layout:topbar.topUpBalance')}</>}
          </span>
        </div>
      )}

      <div className="TopBar__right">
        {/* Rates */}
        {rates && rates.length > 0 && (
          <div className="TopBar__rates-block">
            {rates.map((item, index) => (
              <span key={index} className="TopBar__rate">{item.code}: {item.rate}</span>
            ))}
          </div>
        )}

        {/* Balance badge */}
        <div className="TopBar__balance-group">
          <span className="TopBar__username">
            {userRole === 'teamlead' && <span className="TopBar__role-badge TopBar__role-badge--tl">TL</span>}
            {userName}
          </span>
          <div className="TopBar__balance-badge">
            <DollarIcon />
            <span>{usdtBalance}</span>
            <button className="TopBar__balance-add" onClick={() => navigate('/addbalance')} title={t('layout:topbar.topUpBalanceTitle')}>
              <PlusIcon />
            </button>
          </div>
          {rapiraPayableBalance !== null && rapiraPayableBalance !== undefined && (
            <span className="TopBar__payable-balance" title={t('layout:topbar.rapiraPayableBalance')}>
              {t('layout:topbar.rapiraPayableBalance')}: {rapiraPayableBalance} $
            </span>
          )}
        </div>

        <RefreshIndicator />

        {/* Language selector */}
        <button className="TopBar__theme-btn" onClick={() => setLangModalOpen(true)} title={t('layout:sidebar.actions.language')}>
          <span style={{ fontSize: '14px' }}>{currentLang.flag} {currentLang.label}</span>
        </button>

        {/* Theme toggle */}
        <button className="TopBar__theme-btn" onClick={toggleTheme} title={t('layout:topbar.changeTheme')}>
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>

      <LanguageModal isOpen={langModalOpen} onClose={() => setLangModalOpen(false)} />
    </div>
  );
}
